<template>
    <div
        :data-component="$options.name"
        v-bind="mlmViewTrackingId"
    >
        <ms-sticky-container
            class="left-0 right-0 bg-white"
            :stickyPosition="isScreenL ? StickyPosition.TOP : StickyPosition.BOTTOM"
            :positionType="PositionType.FIXED"
            :isVisible="stickyBarIsVisible"
        >
            <template #content>
                <ms-detail-sticky-bar
                    :estate="estate"
                    :exposeeId="estateDetail.id"
                    :contact="estateDetail.contact"
                    :keyInfo="keyInfo"
                    :componentId="$options.name"
                />
            </template>
        </ms-sticky-container>

        <div class="relative mb-24 screen-l:mb-32">
            <div
                class="absolute -inset-x-16 screen-m:-inset-x-24 top-0 bottom-96 bg-color-signatur-immo-100 screen-l:rounded-10 overflow-hidden"
                data-print-background
            >
                <MsCircle
                    v-if="isScreenL"
                    class="absolute top-0 left-0"
                    data-test-id="circle"
                />
                <MsHouse class="absolute bottom-0 right-0" />
            </div>
            <ms-share-bar
                :headline="shareBarProps.headline"
                :text="shareBarProps.text"
                :exposeeId="estateDetail.id"
                :parentName="$options.name"
                :estate="estate"
                :showReportButton="false"
            />
            <ms-detail-gallery
                :componentId="$options.name"
                :exposeeId="estateDetail.id"
                :images="estateDetail.images"
                :threeSixtyViewUrl="estateDetail.threeSixtyViewUrl"
                :galleryAds="galleryAds"
            />
        </div>
        <p class="mb-8 text-caption text-dovegray">Online seit: {{ estateDetail.createdDateFormatted }}</p>
        <div class="screen-l:grid screen-l:grid-cols-12 screen-l:gap-24">
            <div class="screen-l:col-span-8">
                <div ref="elContactBottom">
                    <section class="basis-full screen-m:basis-[725px] overflow-hidden mr-20">
                        <ms-headline
                            class="mb-16 break-words"
                            type="h1"
                            >{{ estateDetail.title }}</ms-headline
                        >
                        <span class="flex mb-25">
                            <ms-locator class="w-24 h-24 mr-5 shrink-0" />
                            <a
                                class="text-text-l"
                                href="#mapAnchor"
                                @click="trackMapAnchorClick(estateDetail.id)"
                                >{{ estateDetail.address }}</a
                            >
                        </span>
                        <cs-data-sheet
                            :entries="keyInfo"
                            class="mb-40 screen-l:mb-56"
                        />
                    </section>
                    <section
                        v-if="estateDetail.equipment"
                        class="mb-40 screen-m:mb-56"
                    >
                        <ms-headline
                            type="h2"
                            class="mb-16 screen-m:mb-24"
                            >Ausstattung & Merkmale</ms-headline
                        >
                        <div class="max-w-fit flex-row">
                            <cs-tag
                                v-for="item in estateDetail.equipment"
                                :key="item"
                                class="mr-8 mb-8"
                                >{{ item }}</cs-tag
                            >
                        </div>
                    </section>
                    <section class="mb-32 screen-l:mb-40">
                        <template v-if="estateDetail.overview">
                            <ms-headline
                                type="h2"
                                class="mb-16 screen-l:mb-24"
                                >Eckdaten</ms-headline
                            >
                            <div class="mb-16 screen-m:col-span-2">
                                <cs-tabs
                                    v-model="selectedKeyDataOption"
                                    :options="keyDataOptions"
                                >
                                    <table
                                        v-for="(item, tableIndex) in estateDetail.overview"
                                        v-show="showItem(item)"
                                        :key="`i-${tableIndex}`"
                                        class="w-full text-text-l"
                                        aria-label="Table overview"
                                    >
                                        <tbody>
                                            <tr
                                                v-for="(row, index) in item.data"
                                                :key="row.label"
                                            >
                                                <td
                                                    :class="{
                                                        'bg-color-brand-beige-50 rounded-l-[5px]': index % 2 === 0,
                                                    }"
                                                    class="w-1/2 align-top py-8 pl-16 pr-8 font-bold break-words-all"
                                                >
                                                    {{ row.label }}:
                                                </td>
                                                <td
                                                    :class="{
                                                        'bg-color-brand-beige-50 rounded-r-[5px]': index % 2 === 0,
                                                    }"
                                                    class="w-1/2 align-top py-8 pl-8 pr-16 break-words-all"
                                                >
                                                    {{ row.value }}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </cs-tabs>
                            </div>
                            <a
                                v-if="exposeeHasFinancingLink"
                                class="flex font-bold text-text-l hover:underline"
                                :href="partnerLinks.financingLink?.href"
                                :target="partnerLinks.financingLink?.target"
                                data-jest-financing
                                @click="trackFinancingLinkClick(estateDetail.id)"
                                ><ms-euronote class="w-24 h-24 mr-8 shrink-0" />{{
                                    partnerLinks.financingLink?.text
                                }}</a
                            >
                        </template>
                        <ms-map
                            id="mapAnchor"
                            class="screen-m:col-span-2"
                            :address="estateDetail.address"
                            :apiKey="googleApiKey"
                            :componentId="$options.name"
                            :locationCoordinates="estateDetail.locationCoordinates"
                            :isRealEstatePreview="isRealEstatePreview"
                            :relocationLink="partnerLinks.relocationLink"
                        />
                        <div
                            v-if="estateDetail.periodRentInformation"
                            class="p-16 border border-wildsand rounded-10 mb-32 screen-l:mb-40 text-text-l"
                        >
                            <h2 class="mb-16 text-h3 font-bold-headline">Verfügbarkeit</h2>
                            <div class="screen-m:mb-0 screen-m:col-span-2">
                                <table
                                    class="w-full"
                                    aria-label="Table period rent"
                                >
                                    <tbody>
                                        <tr
                                            v-for="(item, index) in estateDetail.periodRentInformation"
                                            :key="item.label"
                                        >
                                            <td
                                                :class="{ 'bg-color-brand-beige-50 rounded-l-[5px]': index % 2 === 0 }"
                                                class="w-1/2 align-top py-8 pl-16 pr-8 font-bold break-words-all"
                                            >
                                                {{ item.label + ':' }}
                                            </td>
                                            <td
                                                :class="{ 'bg-color-brand-beige-50 rounded-r-[5px]': index % 2 === 0 }"
                                                class="w-1/2 align-top py-8 pl-8 pr-16 break-words-all"
                                            >
                                                {{ item.value }}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <ms-expander-box
                            v-if="estateDetail.description"
                            :tealiumData="expanderBoxTrackingData(expanderBox.DESCRIPTION)"
                            class="mb-32 screen-l:mb-40 whitespace-pre-line"
                        >
                            <template #title>
                                <h2 class="mb-16 text-h3 font-bold-headline">Beschreibung</h2>
                            </template>
                            <template #content>
                                {{ estateDetail.description }}
                            </template>
                        </ms-expander-box>
                        <ms-expander-box
                            v-if="estateDetail.equipmentDescription"
                            :tealiumData="expanderBoxTrackingData(expanderBox.EQUIPMENT)"
                            class="mb-32 screen-l:mb-40 whitespace-pre-line"
                        >
                            <template #title>
                                <h2 class="mb-16 text-h3 font-bold-headline">Ausstattung</h2>
                            </template>
                            <template #content>
                                {{ estateDetail.equipmentDescription }}
                            </template>
                        </ms-expander-box>
                        <div
                            v-if="estateDetail.floorPlans"
                            class="ms-box-layout mb-32 screen-l:mb-40"
                        >
                            <ms-detail-floor-plan
                                :exposeeId="estateDetail.id"
                                :images="estateDetail.floorPlans"
                            />
                        </div>
                        <ms-expander-box
                            v-if="estateDetail.locationDescription || estateDetail.poiInformation"
                            :tealiumData="expanderBoxTrackingData(expanderBox.LOCATION)"
                            class="mb-32 screen-l:mb-40 whitespace-pre-line"
                            data-test-id="tableExpanderBox"
                            @update:expanderBox="toggleTable()"
                        >
                            <template #title>
                                <h2 class="mb-16 text-h3 font-bold-headline">Lage</h2>
                            </template>
                            <template #content>
                                <p>{{ estateDetail.locationDescription }}</p>
                                <table
                                    v-if="estateDetail.poiInformation && showTable"
                                    class="w-full mt-20"
                                    aria-label="Table POI information"
                                >
                                    <tbody>
                                        <tr
                                            v-for="(item, index) in estateDetail.poiInformation"
                                            :key="item.label"
                                        >
                                            <td
                                                :class="{ 'bg-color-brand-beige-50 rounded-l-[5px]': index % 2 === 0 }"
                                                class="w-1/2 align-top py-8 pl-16 pr-8 font-bold break-words-all"
                                            >
                                                {{ item.label + ':' }}
                                            </td>
                                            <td
                                                :class="{ 'bg-color-brand-beige-50 rounded-r-[5px]': index % 2 === 0 }"
                                                class="w-1/2 align-top py-8 pl-8 pr-16 break-words-all"
                                            >
                                                {{ item.value }}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </template>
                        </ms-expander-box>
                        <ms-expander-box
                            v-if="estateDetail.furtherDescription"
                            :tealiumData="expanderBoxTrackingData(expanderBox.OTHERS)"
                            class="mb-32 screen-l:mb-40 whitespace-pre-line"
                        >
                            <template #title>
                                <h2 class="mb-16 text-h3 font-bold-headline">Sonstiges</h2>
                            </template>
                            <template #content>
                                {{ estateDetail.furtherDescription }}
                            </template>
                        </ms-expander-box>
                        <div
                            v-if="estateDetail.documents"
                            class="ms-box-layout mb-32 p-16 screen-l:mb-40"
                            data-print-documents
                        >
                            <h2 class="mb-16 text-h3 font-bold-headline">Dokumente</h2>
                            <div class="flex flex-col overflow-hidden screen-m:flex-row flex-wrap screen-m:gap-y-24">
                                <span
                                    v-for="(document, index) in estateDetail.documents"
                                    :key="`${document.url}-${index}`"
                                    class="flex mb-24 text-text-l max-w-full screen-m:mr-24 screen-m:mb-0 last:mb-0 last:mr-0"
                                >
                                    <ms-file-pdf class="w-24 h-24 mr-8 shrink-0" />
                                    <cs-copy-text-link
                                        class="shrink-0"
                                        :class="'no-underline truncate max-w-[92%] screen-m:max-w-full'"
                                        :href="document.url"
                                        :sign="document.sign"
                                        :target="'_blank'"
                                        :isIndex="true"
                                        :isPRG="true"
                                        >{{ document.caption }}</cs-copy-text-link
                                    >
                                </span>
                            </div>
                        </div>
                    </section>
                    <component
                        :is="getComponentByName(adSlot)"
                        v-if="adSlot && !isScreenM"
                        :cmsContent="adSlot"
                        class="mb-32"
                    />
                </div>
                <ms-detail-contact-info
                    class="screen-m:flex mb-32 screen-l:mb-40"
                    :contact="estateDetail.contact"
                    :position="ContactBtnPosition.CONTACT_INFO_BOTTOM"
                    :exposeeId="estateDetail.id"
                    :componentId="$options.name"
                />
                <p class="mb-8 text-caption text-dovegray">
                    Exposé-ID: {{ estateDetail.id }}
                    <template v-if="estateDetail.objectIdExternal"> {{ estateDetail.objectIdExternal }} </template>
                </p>
                <hr class="mb-8" />
                <ms-share-bar
                    :headline="shareBarProps.headline"
                    :text="shareBarProps.text"
                    :exposeeId="estateDetail.id"
                    :parentName="$options.name"
                    :estate="estate"
                    :showReportButton="true"
                />
            </div>
            <ms-detail-contact-info
                ref="elContactTop"
                class="hidden screen-l:block screen-l:col-span-4 screen-m:h-fit"
                :contact="estateDetail.contact"
                :position="ContactBtnPosition.CONTACT_INFO_TOP"
                :exposeeId="estateDetail.id"
                :componentId="$options.name"
            />
        </div>
    </div>
</template>
<script lang="ts" src="./HydratedDetailOnline.ts" />

<style scoped lang="scss">
.ms-box-layout {
    @apply p-16 border border-wildsand rounded-10;
}
</style>
